import React from "react";
import { Clock } from "lucide-react";

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_SHORT = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };
const DAY_LONG = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday" };

export function hasOpeningHours(hours) {
  if (!hours || typeof hours !== "object") return false;
  return DAY_ORDER.some((d) => {
    const day = hours[d];
    if (!day) return false;
    if (day.closed) return true;
    return Boolean((day.open || "").trim() && (day.close || "").trim());
  });
}

export function openingHoursToSpec(hours) {
  // Return Schema.org openingHoursSpecification array.
  if (!hasOpeningHours(hours)) return [];
  return DAY_ORDER
    .map((d) => {
      const day = hours[d];
      if (!day) return null;
      if (day.closed) return null;
      if (!day.open || !day.close) return null;
      return {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: `https://schema.org/${DAY_LONG[d]}`,
        opens: day.open,
        closes: day.close,
      };
    })
    .filter(Boolean);
}

export default function OpeningHours({ hours, variant = "card", testId = "opening-hours" }) {
  if (!hasOpeningHours(hours)) return null;
  if (variant === "footer") {
    // Compact 1-line summary used in footer.
    const today = new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const t = hours?.[today];
    const label = t?.closed
      ? "Closed today"
      : t?.open && t?.close
        ? `Today: ${t.open}–${t.close}`
        : "Hours by appointment";
    return (
      <span data-testid={`${testId}-summary`} className="inline-flex items-center gap-1.5 text-xs text-white/60">
        <Clock className="h-3 w-3 text-teal-light" /> {label}
      </span>
    );
  }
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-navy-50 p-6">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-teal" />
        <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-navy-500">Opening hours</h3>
      </div>
      <dl className="mt-5 grid grid-cols-1 gap-y-1.5">
        {DAY_ORDER.map((d) => {
          const day = hours[d] || {};
          return (
            <div key={d} className="flex items-baseline justify-between gap-4 text-sm">
              <dt className="font-medium text-navy-800 w-28">{DAY_SHORT[d]}</dt>
              <dd className="text-navy-700/80 tabular-nums">
                {day.closed
                  ? <span className="text-navy-500">Closed</span>
                  : day.open && day.close
                    ? `${day.open} — ${day.close}`
                    : <span className="text-navy-300">—</span>}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
